import { useCallback, useState } from 'react';
import { useColumnState } from '~/atoms/hooks/useColumnState';
import { gridApiAtom } from '~/atoms/columnState';
import { captureGridState } from '~/utils/gridStateUtils';
import log from '~/logger';
import { useSetAtom } from 'jotai';
import { showToastAtom } from '~/atoms/pageActions';
/**
 * Hook for managing ag-grid enterprise instance and features
 */
export const useAgGrid = () => {
    const [gridApi, setGridApi] = useState(null);
    const setGlobalGridApi = useSetAtom(gridApiAtom);
    const [selectedRows, setSelectedRows] = useState([]);
    const { updateGridState, loadSavedView } = useColumnState();
    const showToast = useSetAtom(showToastAtom);
    const applyColumnState = useCallback((state) => {
        if (!gridApi)
            return false;
        try {
            gridApi.applyColumnState({
                state: state.columnState,
                applyOrder: true
            });
            if (state.filterModel) {
                gridApi.setFilterModel(state.filterModel);
            }
            log.info('Applied column state successfully');
            return true;
        }
        catch (error) {
            log.error('Failed to apply column state:', error);
            return false;
        }
    }, [gridApi]);
    const onGridReady = useCallback((params, savedViewToApply) => {
        setGridApi(params.api);
        setGlobalGridApi(params.api);
        setTimeout(() => {
            if (params.api) {
                if (savedViewToApply) {
                    try {
                        params.api.applyColumnState({
                            state: savedViewToApply.columnState,
                            applyOrder: true
                        });
                        if (savedViewToApply.filterModel) {
                            params.api.setFilterModel(savedViewToApply.filterModel);
                            setTimeout(() => {
                                const visibleRowCount = params.api.getDisplayedRowCount();
                                if (visibleRowCount === 0) {
                                    showToast({
                                        message: 'Your saved filter matches 0 products. The filter may be outdated.',
                                        type: 'warning'
                                    });
                                }
                            }, 50);
                        }
                        updateGridState(savedViewToApply);
                        log.info('Applied saved view state during grid initialization');
                    }
                    catch (error) {
                        log.error('Failed to apply saved view during grid initialization, falling back to default state:', error);
                        const initialState = captureGridState(params.api);
                        if (initialState) {
                            updateGridState(initialState);
                        }
                    }
                }
                else {
                    const initialState = captureGridState(params.api);
                    if (initialState) {
                        updateGridState(initialState);
                        log.info('Captured initial grid state');
                    }
                }
            }
        }, 100);
    }, [updateGridState, setGlobalGridApi, showToast]);
    const captureCurrentState = useCallback(() => {
        if (!gridApi)
            return null;
        const columnState = gridApi.getColumnState();
        const filterModel = gridApi.getFilterModel();
        const state = {
            columnState,
            rowGroupColumns: [],
            pivotColumns: [],
            filterModel,
            sortModel: []
        };
        updateGridState(state);
        return state;
    }, [gridApi, updateGridState]);
    const getCurrentState = useCallback(() => {
        return captureGridState(gridApi);
    }, [gridApi]);
    const loadView = useCallback(async (viewId) => {
        const state = await loadSavedView(viewId);
        return applyColumnState(state);
    }, [loadSavedView, applyColumnState]);
    const onSelectionChanged = useCallback(() => {
        if (gridApi) {
            const selected = gridApi.getSelectedRows();
            setSelectedRows(selected);
        }
    }, [gridApi]);
    const setQuickFilter = useCallback((text) => {
        if (gridApi) {
            gridApi.setGridOption('quickFilterText', text);
        }
    }, [gridApi]);
    const setRowGroupColumns = useCallback((columns) => {
        if (!gridApi)
            return false;
        log.info('Setting row grouping for columns:', columns);
        return true;
    }, [gridApi]);
    const setAdvancedFilter = useCallback((filterModel) => {
        if (!gridApi)
            return false;
        log.info('Setting advanced filter with enterprise license', filterModel);
        return true;
    }, [gridApi]);
    const refreshData = useCallback(() => {
        if (gridApi) {
            gridApi.refreshCells();
            gridApi.redrawRows();
        }
    }, [gridApi]);
    const autoSizeColumns = useCallback(() => {
        if (gridApi) {
            gridApi.sizeColumnsToFit();
        }
    }, [gridApi]);
    const getFilteredData = useCallback(() => {
        if (!gridApi)
            return [];
        const filteredData = [];
        gridApi.forEachNodeAfterFilter((node) => {
            if (node.data) {
                filteredData.push(node.data);
            }
        });
        return filteredData;
    }, [gridApi]);
    return {
        gridApi,
        selectedRows,
        onGridReady,
        onSelectionChanged,
        setQuickFilter,
        setAdvancedFilter,
        setRowGroupColumns,
        refreshData,
        autoSizeColumns,
        getFilteredData,
        captureCurrentState,
        getCurrentState,
        applyColumnState,
        loadView
    };
};
